import React, { useState } from "react";
import { Target, Loader2, TrendingUp, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Phase 4.16 (L4) — Lead-Gen-Hinweise für Vermittler.
 * Pull-Modell: User klickt „Hinweise generieren" und sieht 3-5 konkrete
 * Action-Empfehlungen, wo er gezielt Verkäufer ansprechen sollte.
 */
export default function VermittlerLeadHintsPanel() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const generate = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/vermittler/lead-hints");
            setData(res.data);
            if (res.data.mocked) {
                toast.message("Lead-Hinweise im Mock-Modus (zu wenig Daten).");
            } else {
                toast.success(`${res.data.hints?.length || 0} KI-Hinweise generiert.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Lead-Gen fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="vermittler-lead-hints">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-twowheels-yellow-foreground" />
                        KI-Lead-Generator
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                        Wir analysieren offene Käufer-Suchaufträge & Markttrends und liefern dir 3-5 konkrete Action-Hinweise, wen du jetzt ansprechen solltest.
                    </p>
                </div>
                <button onClick={generate} disabled={loading}
                    className="bg-twowheels-yellow text-twowheels-yellow-foreground hover:brightness-95 text-sm font-semibold px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0"
                    data-testid="lead-hints-generate-btn">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Lade…</> : <><Target className="w-4 h-4" /> Hinweise generieren</>}
                </button>
            </div>

            {data && (
                <div data-testid="lead-hints-result">
                    {data.open_demands_count != null && (
                        <div className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                            <TrendingUp className="w-3 h-3" />
                            Basierend auf {data.open_demands_count} aktiven Käufer-Suchaufträgen
                            {data.mocked && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm text-[10px] font-semibold">MOCKED</span>}
                        </div>
                    )}
                    {data.hints && data.hints.length > 0 ? (
                        <ul className="space-y-2.5" data-testid="lead-hints-list">
                            {data.hints.map((h, i) => (
                                <li key={i} className="border border-gray-200 rounded-sm p-3 bg-gray-50" data-testid={`lead-hint-${i}`}>
                                    <div className="text-sm font-bold text-gray-900 mb-1">{h.title}</div>
                                    <div className="text-sm text-gray-800 mb-1.5"><span className="text-twowheels-yellow-foreground">→</span> {h.action}</div>
                                    {h.demand_signal && (
                                        <div className="text-xs text-gray-500 italic">Signal: {h.demand_signal}</div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-gray-500 py-4 text-center" data-testid="lead-hints-empty">
                            Aktuell keine konkreten KI-Hinweise – noch zu wenig offene Suchaufträge.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
